import { useState } from "react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { X, ChefHat, ShoppingCart, Clock, Users } from "lucide-react";
import { toast } from "sonner";

const RECIPES = [
  {
    id: 1,
    name: "Pasta Carbonara",
    ingredients: ["pasta", "eggs", "bacon", "cheese"],
    time: 20,
    servings: 4,
    steps: ["Boil pasta", "Cook bacon", "Mix eggs & cheese", "Combine all"]
  },
  {
    id: 2,
    name: "Chicken Salad",
    ingredients: ["chicken", "lettuce", "tomato", "oil"],
    time: 25,
    servings: 2,
    steps: ["Grill chicken", "Chop greens", "Mix & dress"]
  },
  {
    id: 3,
    name: "Stir Fry",
    ingredients: ["broccoli", "carrots", "soy sauce", "oil"],
    time: 15,
    servings: 3,
    steps: ["Chop vegetables", "Heat wok", "Stir fry 7 min"]
  }
];

const Index = () => {
  const [ingredients, setIngredients] = useState<string[]>([]);
  const [input, setInput] = useState("");
  const [shopping, setShopping] = useState<string[]>([]);

  const addIng = () => {
    if (input.trim() && !ingredients.includes(input.toLowerCase())) {
      setIngredients([...ingredients, input.toLowerCase()]);
      setInput("");
    }
  };

  const removeIng = (ing: string) => {
    setIngredients(ingredients.filter(i => i !== ing));
  };

  const getSuggested = () => {
    if (!ingredients.length) return [];
    return RECIPES.filter(r => r.ingredients.some(ing => 
      ingredients.some(sel => sel.includes(ing) || ing.includes(sel))
    ));
  };

  const addMissing = (recipe: typeof RECIPES[0]) => {
    const missing = recipe.ingredients.filter(ing => !ingredients.includes(ing));
    setShopping([...new Set([...shopping, ...missing])]);
    toast.success("Added to shopping list");
  };

  const suggested = getSuggested();

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 to-amber-50 dark:from-slate-950 dark:to-slate-900 p-4 md:p-8">
      <div className="max-w-6xl mx-auto">
        <div className="mb-8 flex items-center gap-3">
          <ChefHat className="w-8 h-8 text-orange-600" />
          <div>
            <h1 className="text-4xl font-bold text-slate-900 dark:text-white">RecipeMatch</h1>
            <p className="text-slate-600 dark:text-slate-400">Find recipes from your ingredients</p>
          </div>
        </div>

        <Tabs defaultValue="recipes" className="w-full">
          <TabsList className="grid w-full max-w-sm grid-cols-2">
            <TabsTrigger value="recipes">Recipes</TabsTrigger>
            <TabsTrigger value="shopping">Shopping List {shopping.length > 0 && `(${shopping.length})`}</TabsTrigger>
          </TabsList>

          <TabsContent value="recipes" className="space-y-6 mt-6">
            <Card className="border-orange-200 dark:border-orange-900">
              <CardHeader>
                <CardTitle>Your Ingredients</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex gap-2">
                  <Input placeholder="Add ingredient..." value={input} onChange={(e) => setInput(e.target.value)} onKeyPress={(e) => e.key === "Enter" && addIng()} />
                  <Button onClick={addIng} className="bg-orange-600 hover:bg-orange-700">Add</Button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {ingredients.map(ing => (
                    <Badge key={ing} variant="secondary">
                      {ing}
                      <X className="w-3 h-3 ml-2 cursor-pointer" onClick={() => removeIng(ing)} />
                    </Badge>
                  ))}
                </div>
              </CardContent>
            </Card>

            <div>
              <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-4">Recipes ({suggested.length})</h2>
              {suggested.length === 0 ? (
                <Card><CardContent className="pt-6 text-center text-slate-600">Add ingredients to see recipes</CardContent></Card>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {suggested.map(r => (
                    <Card key={r.id} className="border-orange-200 dark:border-orange-900">
                      <CardHeader className="bg-orange-50 dark:bg-orange-950">
                        <CardTitle>{r.name}</CardTitle>
                        <div className="flex gap-4 text-sm text-slate-600 mt-2">
                          <span className="flex items-center gap-1"><Clock className="w-4 h-4" />{r.time}min</span>
                          <span className="flex items-center gap-1"><Users className="w-4 h-4" />{r.servings}x</span>
                        </div>
                      </CardHeader>
                      <CardContent className="pt-4 space-y-3">
                        <div>
                          <p className="text-sm font-semibold mb-2">Ingredients:</p>
                          <div className="flex flex-wrap gap-2">
                            {r.ingredients.map(ing => (
                              <Badge key={ing} variant={ingredients.includes(ing) ? "default" : "outline"} className={ingredients.includes(ing) ? "bg-green-600" : ""}>
                                {ing}
                              </Badge>
                            ))}
                          </div>
                        </div>
                        <div>
                          <p className="text-sm font-semibold mb-2">Steps:</p>
                          <ol className="text-sm text-slate-600 space-y-1 list-decimal list-inside">
                            {r.steps.map((s, i) => <li key={i}>{s}</li>)}
                          </ol>
                        </div>
                        <Button onClick={() => addMissing(r)} variant="outline" className="w-full border-orange-200">
                          <ShoppingCart className="w-4 h-4 mr-2" />
                          Add Missing Items
                        </Button>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>

          <TabsContent value="shopping" className="mt-6">
            <Card className="border-orange-200 dark:border-orange-900">
              <CardHeader>
                <CardTitle>Shopping List</CardTitle>
              </CardHeader>
              <CardContent>
                {shopping.length === 0 ? (
                  <p className="text-slate-600 text-center py-8">Your shopping list is empty</p>
                ) : (
                  <div className="space-y-2">
                    {shopping.map(item => (
                      <div key={item} className="flex justify-between p-3 bg-slate-100 dark:bg-slate-800 rounded">
                        <span className="capitalize">{item}</span>
                        <X className="w-4 h-4 cursor-pointer" onClick={() => setShopping(shopping.filter(i => i !== item))} />
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default Index;